import React from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface DrawerTileItem {
  label: string;
  value: string;
  bg: string;
  color: string;
}

export interface DrawerRiskItem {
  label: string;
  value: string;
  w: string;
  color: string;
  note: string;
}

export interface DrawerCompItem {
  label: string;
  value: string;
  icon: string;
  color: string;
}

export interface DrawerPayoutItem {
  date: string;
  amount: string;
  state: string;
  color: string;
  bg: string;
}

export interface DrawerHistoryItem {
  stage: string;
  date: string;
  source: string;
  dot: string;
  line: boolean;
}

export interface DrawerData {
  initial: string;
  cmName: string;
  alias: string;
  accountId: string;
  login: string;
  network: string;
  platformTag: string;
  stage: string;
  stageColor: string;
  stageBg: string;
  stageSource: string;
  status: string;
  statusColor: string;
  toggleBg: string;
  toggleJustify: string;
  toggleLabel: string;
  onToggle: () => void;
  classifyLabel: string;
  onClassify: () => void;
  tiles: DrawerTileItem[];
  risk: DrawerRiskItem[];
  comp: DrawerCompItem[];
  payouts: DrawerPayoutItem[];
  history: DrawerHistoryItem[];
  noPayouts: boolean;
  noHistory: boolean;
  hasTradingData?: boolean;
  hasRiskData?: boolean;
  hasCompData?: boolean;
}

export interface ChartSummary {
  firstPt: number;
  lastPt: number;
  diff: number;
  diffPct: number;
  up: boolean;
}

export interface AccountDetailDrawerProps {
  drawerData: DrawerData | null;
  closeDrawer: () => void;
  drawerRef: React.RefObject<HTMLCanvasElement>;
  showTags?: boolean;
  loading?: boolean;
  hasChartData?: boolean;
  chartSummary?: ChartSummary | null;
}

export const AccountDetailDrawer: React.FC<AccountDetailDrawerProps> = ({
  drawerData,
  closeDrawer,
  drawerRef,
  showTags = true,
  loading = false,
  hasChartData = false,
  chartSummary = null
}) => {
  const { t } = useTranslation();

  if (!drawerData) return null;

  return (
    <>
      <div
        onClick={closeDrawer}
        style={s(`position:fixed;inset:0;background:rgba(1,37,60,0.35);z-index:9998`)}
      ></div>
      <aside
        style={s(
          `position:fixed;top:0;right:0;bottom:0;width:min(540px,100%);background:#F7F8FA;z-index:9999;overflow-y:auto;box-shadow:-12px 0 40px rgba(1,37,60,0.2);display:flex;flex-direction:column`
        )}
      >
        <div
          style={s(
            `background:#fff;padding:20px 24px;border-bottom:1px solid #EEF0F3;display:flex;flex-direction:column;gap:14px;position:sticky;top:0;z-index:2`
          )}
        >
          <div style={s(`display:flex;align-items:flex-start;gap:12px`)}>
            <span
              style={s(
                `width:44px;height:44px;border-radius:50%;background:#EEF0F3;font-weight:700;font-size:16px;display:flex;align-items:center;justify-content:center;flex:none`
              )}
            >
              {drawerData.initial}
            </span>
            <div style={s(`flex:1;min-width:0;display:flex;flex-direction:column;gap:3px`)}>
              <div style={s(`display:flex;align-items:center;gap:8px`)}>
                <span style={s(`font-size:17px;font-weight:700;color:#0F1B2D`)}>
                  {drawerData.cmName} - {drawerData.alias}
                </span>
                {Boolean(loading) && (
                  <span
                    style={s(
                      `font-size:11px;font-weight:600;color:#B8893A;background:#FFF7EA;border:1px solid #F6DFB8;border-radius:4px;padding:1px 6px`
                    )}
                  >
                    Loading report...
                  </span>
                )}
              </div>
              <span style={s(`font-size:12.5px;color:#6B7280`)}>
                Login {drawerData.login}
              </span>
            </div>
            <button
              onClick={closeDrawer}
              style={s(
                `width:34px;height:34px;border-radius:8px;border:1px solid #E3E6EA;background:#fff;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;flex:none`
              )}
            >
              <i className="icon-x"></i>
            </button>
          </div>
          <div style={s(`display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-size:12px`)}>
            <span
              style={s(
                `font-size:10.5px;font-weight:700;color:#1F7A6E;background:#DDF1EE;padding:3px 7px;border-radius:4px`
              )}
            >
              #{drawerData.platformTag}
            </span>
            <span
              style={s(
                `font-weight:600;color:${drawerData.stageColor};background:${drawerData.stageBg};padding:3px 9px;border-radius:10px`
              )}
            >
              {t('managerNetwork.drawer.stage', 'Giai đoạn: {{stage}}', { stage: drawerData.stage })}
            </span>
          </div>

          {/* Action buttons (tạm ẩn) */}
          {/*
          <div style={s(`display:flex;flex-wrap:wrap;gap:10px`)}>
            <button
              onClick={drawerData.onToggle}
              style={s(
                `height:36px;padding:0 12px;border:1px solid #E3E6EA;border-radius:8px;background:#fff;color:#1B2430;font-size:13px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:8px`
              )}
            >
              <span
                style={s(
                  `width:30px;height:18px;border-radius:9px;padding:2px;background:${drawerData.toggleBg};display:flex;justify-content:${drawerData.toggleJustify}`
                )}
              >
                <span style={s(`width:14px;height:14px;border-radius:50%;background:#fff`)}></span>
              </span>
              {drawerData.toggleLabel}
            </button>
            <button
              onClick={drawerData.onClassify}
              style={s(
                `height:36px;padding:0 12px;border:1px solid #E3E6EA;border-radius:8px;background:#fff;color:#1B2430;font-size:13px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:6px`
              )}
            >
              <i className="icon-tags"></i>
              {drawerData.classifyLabel}
            </button>
          </div>
          */}
        </div>

        <div style={s(`padding:20px 24px;display:flex;flex-direction:column;gap:16px`)}>
          <div
            style={s(
              `background:#fff;border:1px solid #E6E8EC;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:12px`
            )}
          >
            <div style={s(`display:flex;justify-content:space-between;align-items:center`)}>
              <span style={s(`font-size:14px;font-weight:600;color:#0F1B2D`)}>
                {t('managerNetwork.drawer.tradingData', 'Trading data')}
              </span>
              {Boolean(showTags) && (
                <span
                  style={s(
                    `font-size:10.5px;font-weight:600;color:#6B7280;border:1px solid #E3E6EA;border-radius:4px;padding:2px 6px`
                  )}
                >
                  RAW · BOT/API
                </span>
              )}
            </div>
            {loading ? (
              <div
                style={s(
                  `height:72px;display:flex;align-items:center;justify-content:center;gap:8px;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <i className="icon-refresh" style={s(`font-size:16px;color:#B8893A;animation:spin 1s linear infinite`)}></i>
                <span style={s(`font-size:12.5px;color:#6B7280;font-weight:500`)}>
                  {t('managerNetwork.drawer.loadingTradingData', 'Đang tải dữ liệu...')}
                </span>
              </div>
            ) : !drawerData.hasTradingData || !drawerData.tiles || drawerData.tiles.length === 0 ? (
              <div
                style={s(
                  `height:72px;display:flex;align-items:center;justify-content:center;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <span style={s(`font-size:13px;font-weight:500;color:#94A3B8`)}>
                  {t('managerNetwork.drawer.noTradingData', 'Không có dữ liệu')}
                </span>
              </div>
            ) : (
              <div style={s(`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px`)}>
                {drawerData.tiles.map((t_item, t_idx) => (
                  <div
                    key={t_idx}
                    style={s(
                      `background:${t_item.bg};border-radius:8px;padding:10px 12px;display:flex;flex-direction:column;gap:4px;min-width:0`
                    )}
                  >
                    <span style={s(`font-size:11.5px;color:#6B7280`)}>{t_item.label}</span>
                    <span
                      style={s(
                        `font-size:14px;font-weight:700;color:${t_item.color};white-space:nowrap;overflow:hidden;text-overflow:ellipsis`
                      )}
                    >
                      {t_item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={s(
              `background:#fff;border:1px solid #E6E8EC;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:10px`
            )}
          >
            <div style={s(`display:flex;justify-content:space-between;align-items:flex-start`)}>
              <div style={s(`display:flex;flex-direction:column;gap:3px`)}>
                <span style={s(`font-size:14px;font-weight:600;color:#0F1B2D`)}>
                  {t('managerNetwork.drawer.equityPerf30d', 'Equity performance · 30 days')}
                </span>
                {Boolean(!loading && hasChartData && chartSummary) && (
                  <div style={s(`display:flex;align-items:center;gap:6px;font-size:12px`)}>
                    <span style={s(`font-weight:700;color:${chartSummary?.up ? '#219354' : '#D9483B'}`)}>
                      {(chartSummary?.diff ?? 0) >= 0 ? '+' : ''}${Math.round(chartSummary?.diff ?? 0).toLocaleString()}
                    </span>
                    <span
                      style={s(
                        `font-size:11px;font-weight:600;padding:1px 6px;border-radius:4px;color:${
                          chartSummary?.up ? '#166534' : '#991B1B'
                        };background:${chartSummary?.up ? '#DCFCE7' : '#FEE2E2'}`
                      )}
                    >
                      {(chartSummary?.diffPct ?? 0) >= 0 ? '+' : ''}{(chartSummary?.diffPct ?? 0).toFixed(2)}%
                    </span>
                  </div>
                )}
              </div>
              {Boolean(showTags) && (
                <span
                  style={s(
                    `font-size:10.5px;font-weight:600;color:#6B7280;border:1px solid #E3E6EA;border-radius:4px;padding:2px 6px`
                  )}
                >
                  CALCULATED
                </span>
              )}
            </div>

            {loading ? (
              <div
                style={s(
                  `height:160px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <i className="icon-refresh" style={s(`font-size:18px;color:#B8893A;animation:spin 1s linear infinite`)}></i>
                <span style={s(`font-size:12.5px;color:#6B7280;font-weight:500`)}>
                  {t('managerNetwork.drawer.loadingChart', 'Đang tải dữ liệu biểu đồ...')}
                </span>
              </div>
            ) : !hasChartData ? (
              <div
                style={s(
                  `height:160px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <div
                  style={s(
                    `width:38px;height:38px;border-radius:50%;background:#F1F5F9;display:flex;align-items:center;justify-content:center;margin-bottom:2px`
                  )}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#94A3B8"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                </div>
                <span style={s(`font-size:13px;font-weight:600;color:#475569`)}>
                  {t('managerNetwork.drawer.noChartData', 'Không có dữ liệu')}
                </span>
                <span style={s(`font-size:11.5px;color:#94A3B8`)}>
                  {t('managerNetwork.drawer.noChartDataSub', 'Chưa có lịch sử giao dịch để vẽ biểu đồ')}
                </span>
              </div>
            ) : null}

            <div
              style={s(
                `position:relative;height:160px;min-width:0;overflow:hidden;display:${
                  !loading && hasChartData ? 'block' : 'none'
                }`
              )}
            >
              <canvas ref={drawerRef}></canvas>
            </div>
          </div>

          <div
            style={s(
              `background:#fff;border:1px solid #E6E8EC;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:12px`
            )}
          >
            <div style={s(`display:flex;justify-content:space-between;align-items:center`)}>
              <span style={s(`font-size:14px;font-weight:600;color:#0F1B2D`)}>
                {t('managerNetwork.drawer.riskIndicators', 'Risk indicators')}
              </span>
              {Boolean(showTags) && (
                <span
                  style={s(
                    `font-size:10.5px;font-weight:600;color:#6B7280;border:1px solid #E3E6EA;border-radius:4px;padding:2px 6px`
                  )}
                >
                  CALCULATED
                </span>
              )}
            </div>
            {loading ? (
              <div
                style={s(
                  `height:72px;display:flex;align-items:center;justify-content:center;gap:8px;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <i className="icon-refresh" style={s(`font-size:16px;color:#B8893A;animation:spin 1s linear infinite`)}></i>
                <span style={s(`font-size:12.5px;color:#6B7280;font-weight:500`)}>
                  {t('managerNetwork.drawer.loadingRiskData', 'Đang tải dữ liệu...')}
                </span>
              </div>
            ) : !drawerData.hasRiskData || !drawerData.risk || drawerData.risk.length === 0 ? (
              <div
                style={s(
                  `height:72px;display:flex;align-items:center;justify-content:center;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <span style={s(`font-size:13px;font-weight:500;color:#94A3B8`)}>
                  {t('managerNetwork.drawer.noRiskData', 'Không có dữ liệu')}
                </span>
              </div>
            ) : (
              drawerData.risk.map((rk, rk_idx) => (
                <div key={rk_idx} style={s(`display:flex;flex-direction:column;gap:6px`)}>
                  <div style={s(`display:flex;justify-content:space-between;font-size:13px`)}>
                    <span style={s(`color:#1B2430`)}>{rk.label}</span>
                    <span style={s(`font-weight:600;color:${rk.color}`)}>{rk.value}</span>
                  </div>
                  <div style={s(`height:6px;border-radius:3px;background:#EEF0F3;overflow:hidden`)}>
                    <div style={s(`height:100%;width:${rk.w};background:${rk.color};border-radius:3px`)}></div>
                  </div>
                  {Boolean(rk.note) && <span style={s(`font-size:11.5px;color:#9AA3AF`)}>{rk.note}</span>}
                </div>
              ))
            )}
          </div>

          <div
            style={s(
              `background:#fff;border:1px solid #E6E8EC;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:10px`
            )}
          >
            <div style={s(`display:flex;justify-content:space-between;align-items:center`)}>
              <span style={s(`font-size:14px;font-weight:600;color:#0F1B2D`)}>
                {t('managerNetwork.drawer.complianceIndicators', 'Compliance indicators')}
              </span>
              {Boolean(showTags) && (
                <span
                  style={s(
                    `font-size:10.5px;font-weight:600;color:#6B7280;border:1px solid #E3E6EA;border-radius:4px;padding:2px 6px`
                  )}
                >
                  CALCULATED
                </span>
              )}
            </div>
            {loading ? (
              <div
                style={s(
                  `height:72px;display:flex;align-items:center;justify-content:center;gap:8px;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <i className="icon-refresh" style={s(`font-size:16px;color:#B8893A;animation:spin 1s linear infinite`)}></i>
                <span style={s(`font-size:12.5px;color:#6B7280;font-weight:500`)}>
                  {t('managerNetwork.drawer.loadingCompData', 'Đang tải dữ liệu...')}
                </span>
              </div>
            ) : !drawerData.hasCompData || !drawerData.comp || drawerData.comp.length === 0 ? (
              <div
                style={s(
                  `height:72px;display:flex;align-items:center;justify-content:center;background:#FAFBFD;border-radius:8px;border:1px dashed #E2E8F0`
                )}
              >
                <span style={s(`font-size:13px;font-weight:500;color:#94A3B8`)}>
                  {t('managerNetwork.drawer.noComplianceData', 'Không có dữ liệu')}
                </span>
              </div>
            ) : (
              drawerData.comp.map((cp, cp_idx) => (
                <div key={cp_idx} style={s(`display:flex;align-items:center;gap:10px;font-size:13px`)}>
                  <i className={`icon-${cp.icon}`} style={s(`color:${cp.color};font-size:16px`)}></i>
                  <span style={s(`flex:1;color:#1B2430`)}>{cp.label}</span>
                  <span style={s(`font-weight:600;color:${cp.color}`)}>{cp.value}</span>
                </div>
              ))
            )}
          </div>

          {/* Tạm ẩn Lịch sử Payout */}
          {/*
          <div
            style={s(
              `background:#fff;border:1px solid #E6E8EC;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:10px`
            )}
          >
            <div style={s(`display:flex;justify-content:space-between;align-items:center`)}>
              <span style={s(`font-size:14px;font-weight:600;color:#0F1B2D`)}>
                {t('managerNetwork.drawer.payouts', 'Payouts')}
              </span>
              {Boolean(showTags) && (
                <span
                  style={s(
                    `font-size:10.5px;font-weight:600;color:#6B7280;border:1px solid #E3E6EA;border-radius:4px;padding:2px 6px`
                  )}
                >
                  BUSINESS · LUMIR
                </span>
              )}
            </div>
            {drawerData.payouts?.map((po, po_idx) => (
              <React.Fragment key={po_idx}>
                <div style={s(`display:flex;align-items:center;gap:10px;font-size:13px`)}>
                  <span style={s(`flex:1;color:#1B2430`)}>{po.date}</span>
                  <span style={s(`font-weight:600`)}>{po.amount}</span>
                  <span
                    style={s(
                      `font-size:11.5px;font-weight:600;color:${po.color};background:${po.bg};padding:2px 8px;border-radius:10px`
                    )}
                  >
                    {po.state}
                  </span>
                </div>
              </React.Fragment>
            ))}
            {Boolean(drawerData.noPayouts) && (
              <span style={s(`font-size:13px;color:#9AA3AF`)}>
                {t('managerNetwork.drawer.noPayoutRecords', 'No payout records.')}
              </span>
            )}
          </div>
          */}

          {/* Tạm ẩn Lịch sử giai đoạn */}
          {/*
          <div
            style={s(
              `background:#fff;border:1px solid #E6E8EC;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:4px`
            )}
          >
            <div style={s(`display:flex;justify-content:space-between;align-items:center;margin-bottom:8px`)}>
              <span style={s(`font-size:14px;font-weight:600;color:#0F1B2D`)}>
                {t('managerNetwork.drawer.stageHistory', 'Stage history')}
              </span>
              {Boolean(showTags) && (
                <span
                  style={s(
                    `font-size:10.5px;font-weight:600;color:#6B7280;border:1px solid #E3E6EA;border-radius:4px;padding:2px 6px`
                  )}
                >
                  BUSINESS · LUMIR
                </span>
              )}
            </div>
            {drawerData.history?.map((h, h_idx) => (
              <React.Fragment key={h_idx}>
                <div style={s(`display:flex;gap:12px`)}>
                  <div style={s(`display:flex;flex-direction:column;align-items:center;width:14px`)}>
                    <span
                      style={s(
                        `width:12px;height:12px;border-radius:50%;border:2px solid ${h.dot};background:#fff;margin-top:3px`
                      )}
                    ></span>
                    {Boolean(h.line) && (
                      <span style={s(`flex:1;width:2px;background:#EEF0F3;min-height:22px`)}></span>
                    )}
                  </div>
                  <div
                    style={s(
                      `flex:1;display:flex;justify-content:space-between;gap:8px;padding-bottom:12px;font-size:13px`
                    )}
                  >
                    <span style={s(`font-weight:600;color:#0F1B2D`)}>{h.stage}</span>
                    <span style={s(`color:#6B7280`)}>
                      {h.date} · {h.source}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            ))}
            {Boolean(drawerData.noHistory) && (
              <span style={s(`font-size:13px;color:#A8660B;display:flex;align-items:center;gap:6px`)}>
                <i className="icon-triangle-alert"></i>
                {t('managerNetwork.drawer.noConfirmedStage', 'No confirmed stage yet. Requires admin classification.')}
              </span>
            )}
          </div>
          */}
        </div>
      </aside>
    </>
  );
};
